import {
  ApiError,
  DialogResults,
  sendNotification,
  useApiClient,
  useEntityMetadata,
  useSignalrTopic,
  useTranslation,
} from '@msdk/common'
import { Entity, EntityModel, OptionsetValue } from '@msdk/definitions'
import {
  ButtonPrimary,
  ButtonSecondary,
  Card,
  CardActions,
  CardHeader,
  Chip,
  IconSelector,
  styled,
  uiTokens,
} from '@msdk/ui'
import { useEffect, useState } from 'react'
import { useWizard } from 'react-use-wizard'

import { useCodeContext } from './CodeContext'
import { CodeValidation } from './CodeValidation'
import { FooterPopup } from './FooterPopup'
import { HeaderPopup } from './HeaderPopup'
import { ModalStepProps, StyledContent, StyledMessageTypography } from './shared'

const StyledOptionCard = styled(Card)`
  &.MuiPaper-root {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${uiTokens.spacing.gap.small}px;
    width: 100%;
    box-sizing: border-box;
    padding: ${uiTokens.spacing.padding.medium}px;
    border-radius: ${uiTokens.radius.large}px;
    border: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
    background: ${uiTokens.uiControl.outlined.enabled};
    box-shadow: ${uiTokens.elevation.x[0]} ${uiTokens.elevation.y[100]}px ${uiTokens.elevation.blur[100]}px
      ${uiTokens.elevation.spread[100]} ${uiTokens.uiSurface.effect.shadow};
  }
`

const StyledOptionBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${uiTokens.spacing.gap.medium}px;
  width: 100%;
`

const StyledOptionTitle = styled(CardHeader)`
  padding: 0;
  .MuiCardHeader-title {
    color: ${uiTokens.uiContent.accentIntense};
  }
`

const StyledCardActions = styled(CardActions)`
  padding: 0;
  .MuiCardHeader-title {
    color: ${uiTokens.uiContent.accentIntense};
  }
`

const StyledButtonSecondary = styled(ButtonSecondary)`
  gap: ${uiTokens.spacing.gap.xSmall}px;
`

const StyledOptionHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${uiTokens.spacing.gap.small}px;
  width: 100%;
`

interface AddSignInMethodPopupProps extends ModalStepProps {
  uploadedPhoto: Entity | undefined
  onUploadedPhotoChange: (value: Entity | undefined) => void
}

export function AddSignInMethodPopup({ onUploadedPhotoChange, ...props }: AddSignInMethodPopupProps) {
  const { goToStep } = useWizard()
  const t = useTranslation()
  const apiClient = useApiClient()
  const { face, nfc, generateCode, invalidateCode } = useCodeContext()
  const [photoUploaded, setPhotoUploaded] = useState<Entity | undefined>(undefined)
  const [isPhotoEnrolled, setIsPhotoEnrolled] = useState(false)
  const [NFCEnrolled, setNFCEnrolled] = useState<Entity | undefined>(undefined)
  const NFCEntityModel = useEntityMetadata('USER_NFC_ID') as EntityModel
  const faceImageEntityModel = useEntityMetadata('USER_FACE_IMAGE') as EntityModel
  const dialogTitle = t('LBL_NGID_OTHER_METHODS', 'Other methods for dialysis machine access')
  const dialogMessage = t(
    'LBL_NGID_MACHINE_LOGIN_FASTER',
    'Log in faster to the dialysis machine. Activate and set up other access methods.',
  )
  const secondaryBtnLabel =
    (photoUploaded || (face.code && !face.isCodeExpired)) && (NFCEnrolled || (nfc.code && !nfc.isCodeExpired))
      ? t('LBL_NGID_CLOSE_BTN', 'Close')
      : t('LBL_NGID_SKIP_BTN', 'Skip')

  const handleSetUpFace = () => {
    goToStep(4)
  }

  const handleSetUpNFC = async () => {
    sendNotification({
      message: t('LBL_NGID_MACHINE_NFC_CARD_NOTIFIC_MSG', 'Finish the activation process at the dialysis machine.'),
      status: 'success',
      title: t('LBL_NGID_NFC_CARD_NOTIFIC_TITLE', 'NFC card activation code created'),
    })
    await generateCode('nfc')
    invalidateCode('face')
    goToStep(8)
  }

  const handleViewCode = (type: 'face' | 'nfc') => {
    goToStep(type === 'face' ? 7 : 8)
  }

  const handleGetNewCode = async (type: 'face' | 'nfc') => {
    invalidateCode(type)
    await generateCode(type)
    goToStep(type === 'face' ? 7 : 8)
  }

  const handleDisablePhoto = async () => {
    photoUploaded!.fields['ENTITY_STATUS'] = new OptionsetValue('INACTIVE', 'ENTITY_STATUS')
    let res = await apiClient.entity.forceSaveEntities('USM', faceImageEntityModel, [photoUploaded!])
    if (!(res instanceof ApiError)) {
      res = await apiClient.entity.deleteEntity('USM', 'USER_FACE_IMAGE', photoUploaded!.id)
    }
    if (res instanceof ApiError) {
      console.error('[AddSignInMethodPopup] - Error deleting photo', res)
      sendNotification({
        message: t('LBL_NGID_ERROR_DELETING_PHOTO', 'Error deleting photo'),
        status: 'error',
      })
    } else {
      setIsPhotoEnrolled(false)
      setPhotoUploaded(undefined)
    }
  }

  const handleDisableNFC = async () => {
    NFCEnrolled!.fields['ENTITY_STATUS'] = new OptionsetValue('INACTIVE', 'ENTITY_STATUS')
    let res = await apiClient.entity.forceSaveEntities('USM', NFCEntityModel, [NFCEnrolled!])
    if (!(res instanceof ApiError)) {
      res = await apiClient.entity.deleteEntity('USM', 'USER_NFC_ID', NFCEnrolled!.id)
    }
    if (res instanceof ApiError) {
      console.error('[AddSignInMethodPopup] - Error deleting NFC card', res)
      sendNotification({
        message: t('LBL_NGID_ERROR_DELETING_NFC', 'Error deleting NFC card'),
        status: 'error',
      })
    } else {
      setNFCEnrolled(undefined)
    }
  }

  const getUploadedPhoto = async () => {
    let photo = await apiClient.read<Entity | undefined>(`USM/api/NGID/GetUploadedPhoto`)
    if (photo instanceof ApiError) {
      console.error('[AddSignInMethodPopup] - Error checking if photo is uploaded', photo)
      sendNotification({
        message: t('LBL_NGID_ERROR_CHECKING_PHOTO_UPLOAD', 'Error checking if photo is uploaded'),
        status: 'error',
      })
      photo = undefined
    }
    onUploadedPhotoChange(photo?.id ? (photo as Entity) : undefined)
    setPhotoUploaded(typeof photo === 'object' ? photo : undefined)
  }

  const checkIfPhotoIsEnrolled = async () => {
    const res = await apiClient.read<boolean>(`USM/api/NGID/IsPhotoEnrolled`)
    if (res instanceof ApiError) {
      console.error('[AddSignInMethodPopup] - Error checking if photo is enrolled', res)
      sendNotification({
        message: t('LBL_NGID_ERROR_CHECKING_PHOTO_ENROLLMENT', 'Error checking if photo is enrolled'),
        status: 'error',
      })
      setIsPhotoEnrolled(false)
    }
    setIsPhotoEnrolled(res as boolean)
  }

  const getNFCEnrolled = async () => {
    let nfcData = await apiClient.read<Entity | undefined>(`USM/api/NGID/GetNFCEnrolled`)
    if (nfcData instanceof ApiError) {
      console.error('[AddSignInMethodPopup] - Error checking if NFC is enrolled', nfcData)
      sendNotification({
        message: t('LBL_NGID_ERROR_CHECKING_NFC_ENROLLMENT', 'Error checking if NFC is enrolled'),
        status: 'error',
      })
      nfcData = undefined
    }
    setNFCEnrolled(typeof nfcData === 'object' ? nfcData : undefined)
    if (typeof nfcData === 'object') {
      invalidateCode('nfc')
    }
  }

  useEffect(() => {
    checkIfPhotoIsEnrolled()
    getUploadedPhoto()
    getNFCEnrolled()
  }, [])

  useSignalrTopic('user-biometric-data-updated', () => {
    checkIfPhotoIsEnrolled()
    invalidateCode('face')
    getUploadedPhoto()
  })

  useSignalrTopic('user-nfc-id-updated', () => {
    getNFCEnrolled()
  })

  return (
    <>
      <HeaderPopup title={dialogTitle} modalRef={props.modalRef} />
      <StyledContent>
        <StyledMessageTypography variant="body1">{dialogMessage}</StyledMessageTypography>
        {/* Face recognition */}
        <StyledOptionCard>
          <StyledOptionBody>
            <StyledOptionHeader>
              <StyledOptionTitle
                title={t('LBL_NGID_FACE_RECO_TITLE', 'Face recognition')}
                subheader={t('LBL_NGID_FACE_RECO_DESCR', 'Touch-less biometric login.')}
              />
              {isPhotoEnrolled && face.code === undefined && (
                <Chip size="medium" label={t('LBL_NGID_ACTIVE_METHOD', 'Active')} color="success" />
              )}
              {((face.code != undefined && !face.isCodeExpired) ||
                (photoUploaded != undefined && !isPhotoEnrolled)) && (
                <Chip
                  size="medium"
                  label={t('LBL_NGID_IN_PROGRESS_METHOD', 'In progress')}
                  color="info"
                  variant="filled"
                />
              )}
              {photoUploaded == undefined && face.code != undefined && face.isCodeExpired && (
                <Chip size="medium" label={t('LBL_NGID_EXPIRED_METHOD', 'Expired')} color="warning" />
              )}
            </StyledOptionHeader>
            <StyledCardActions>
              {photoUploaded == undefined && face.code == undefined && (
                <ButtonPrimary onClick={handleSetUpFace}>{t('LBL_NGID_SETUP_BTN', 'Set up')}</ButtonPrimary>
              )}
              {photoUploaded !== undefined && face.code === undefined && (
                <>
                  <StyledButtonSecondary onClick={handleDisablePhoto}>
                    <IconSelector iconName="Block" color={uiTokens.uiContent.accent} />
                    {t('LBL_NGID_DISABLE', 'Disable')}
                  </StyledButtonSecondary>
                  <StyledButtonSecondary onClick={() => goToStep(6)}>
                    <IconSelector iconName="EditOutlined" color={uiTokens.uiContent.accent} />
                    {t('LBL_NGID_UPDATE_PHOTO', 'Update photo')}
                  </StyledButtonSecondary>
                </>
              )}
              {face.code !== undefined && !face.isCodeExpired && (
                <>
                  <StyledButtonSecondary onClick={() => handleViewCode('face')}>
                    <IconSelector iconName="VisibilityOnIcon" color={uiTokens.uiContent.accent} />
                    {t('LBL_NGID_VIEW_CODE', 'View code')}
                  </StyledButtonSecondary>
                  <CodeValidation codeType="face" />
                </>
              )}
              {face.code != undefined && face.isCodeExpired && (
                <StyledButtonSecondary onClick={() => handleGetNewCode('face')}>
                  {t('LBL_NGID_GET_NEW_CODE', 'Get new code')}
                </StyledButtonSecondary>
              )}
            </StyledCardActions>
          </StyledOptionBody>
        </StyledOptionCard>
        {/* NFC card */}
        <StyledOptionCard>
          <StyledOptionBody>
            <StyledOptionHeader>
              <StyledOptionTitle
                title={t('LBL_NGID_NFC_CARD_TITLE', 'NFC card')}
                subheader={t('LBL_NGID_NFC_CARD_DESCR', 'Log in using your card.')}
              />
              {NFCEnrolled != undefined && (
                <Chip size="medium" label={t('LBL_NGID_ACTIVE_METHOD', 'Active')} color="success" />
              )}
              {NFCEnrolled == undefined && nfc.code != undefined && !nfc.isCodeExpired && (
                <Chip size="medium" label={t('LBL_NGID_IN_PROGRESS_METHOD', 'In progress')} color="info" />
              )}
              {NFCEnrolled == undefined && nfc.code != undefined && nfc.isCodeExpired && (
                <Chip size="medium" label={t('LBL_NGID_EXPIRED_METHOD', 'Expired')} color="warning" />
              )}
            </StyledOptionHeader>
            <StyledCardActions>
              {NFCEnrolled == undefined && nfc.code == undefined && (
                <ButtonPrimary onClick={handleSetUpNFC}>{t('LBL_NGID_SETUP_BTN', 'Set up')}</ButtonPrimary>
              )}
              {NFCEnrolled == undefined && nfc.code != undefined && !nfc.isCodeExpired && (
                <>
                  <StyledButtonSecondary onClick={() => handleViewCode('nfc')}>
                    <IconSelector iconName="VisibilityOnIcon" color={uiTokens.uiContent.accent} />
                    {t('LBL_NGID_VIEW_CODE', 'View code')}
                  </StyledButtonSecondary>
                  <CodeValidation codeType="nfc" />
                </>
              )}
              {nfc.code != undefined && nfc.isCodeExpired && (
                <>
                  <StyledButtonSecondary onClick={() => handleGetNewCode('nfc')}>
                    {t('LBL_NGID_GET_NEW_CODE', 'Get new code')}
                  </StyledButtonSecondary>
                </>
              )}
              {NFCEnrolled != undefined && (
                <StyledButtonSecondary onClick={handleDisableNFC}>
                  <IconSelector iconName="Block" color={uiTokens.uiContent.accent} />
                  {t('LBL_NGID_DISABLE', 'Disable')}
                </StyledButtonSecondary>
              )}
            </StyledCardActions>
          </StyledOptionBody>
        </StyledOptionCard>
      </StyledContent>
      <FooterPopup
        secondary={{
          label: secondaryBtnLabel,
          onClick: () => props.modalRef?.close(DialogResults.Ok),
        }}
      />
    </>
  )
}
